'use client';

import { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useParams } from '@/compat/navigation';
import { useProjects } from '@pilot/api';

export interface ProjectInfo {
  id: string;
  name: string;
  code: string;
  description?: string;
  icon?: string;
}

export interface ProjectContextValue {
  projects: ProjectInfo[];
  selectedProject: ProjectInfo | null;
  setSelectedProject: (project: ProjectInfo | null) => void;
  refreshProjects: () => Promise<void>;
  isLoading: boolean;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

interface ProjectProviderProps {
  children: ReactNode;
  spaceSlug: string;
}

export function ProjectProvider({ children, spaceSlug }: ProjectProviderProps) {
  const params = useParams();
  const projectCode = params.projectCode as string | undefined;
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [selectedProject, setSelectedProjectState] = useState<ProjectInfo | null>(null);
  const { data: fetchedProjects, isLoading, refetch } = useProjects(spaceSlug);

  useEffect(() => {
    if (fetchedProjects) {
      setProjects(fetchedProjects);
    }
  }, [fetchedProjects]);

  const refreshProjects = useCallback(async () => {
    await refetch();
  }, [refetch]);

  useEffect(() => {
    if (projects.length > 0) {
      if (projectCode) {
        const project = projects.find((p) => p.code === projectCode);
        if (project && project.id !== selectedProject?.id) {
          setSelectedProjectState(project);
        }
      } else if (!selectedProject) {
        setSelectedProjectState(projects[0]);
      }
    }
  }, [projectCode, projects, selectedProject]);

  const setSelectedProject = useCallback((project: ProjectInfo | null) => {
    setSelectedProjectState(project);
  }, []);

  return (
    <ProjectContext.Provider
      value={{
        projects,
        selectedProject,
        setSelectedProject,
        refreshProjects,
        isLoading,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject(): ProjectContextValue {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}

export function useProjectOptional(): ProjectContextValue | null {
  return useContext(ProjectContext);
}
